// src/components/intake/AnswerView.jsx
import React from "react";
import { cn } from "../ui";
import {
  ABOUT_YOU_FIELDS,
  PART_KEYS,
  amountValue,
  draftFrom,
  formatAmount,
  formatMoney,
  isCleared,
} from "./intakeRules";
import {
  AMOUNT_TOTAL,
  NO,
  Q3_PARTS,
  Q4_PLANS_LABEL,
  QP_NA,
  RANKED_PILL,
  SOMETHING_ELSE,
  YES,
} from "./intakeCopy";

// One page's answer, read-only (DESIGN.md "Questionnaire": pills for choices,
// figure rows for amounts, term and description pairs for open answers), or
// N/A when the page has none. Used by the dashboard's answer cards, the
// Questions page and the staff view.

function Pills({ items }) {
  return (
    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-2 tw-p-0">
      {items.map((item) => (
        <li
          key={item}
          className="tw-rounded-pill tw-bg-surface-selected tw-px-2 tw-py-px tw-text-xs tw-font-bold tw-text-text"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function Pairs({ pairs }) {
  return (
    <dl className="tw-m-0 tw-grid tw-gap-3">
      {pairs.map(([term, value]) => (
        <div key={term}>
          <dt className="tw-text-sm tw-font-semibold">{term}</dt>
          <dd className="tw-m-0 tw-text-sm tw-text-text-muted">
            {value || QP_NA}
          </dd>
        </div>
      ))}
    </dl>
  );
}

const NA = () => (
  <p className="tw-m-0 tw-text-sm tw-text-text-muted">{QP_NA}</p>
);

function Choices({ items, other, otherLabel = SOMETHING_ELSE }) {
  if (!items.length && !other) return <NA />;
  return (
    <div className="tw-grid tw-gap-3">
      {items.length > 0 && <Pills items={items} />}
      {other && <Pairs pairs={[[otherLabel, other]]} />}
    </div>
  );
}

export default function AnswerView({ page, answer }) {
  if (!answer || isCleared(page, draftFrom(page, answer))) return <NA />;
  switch (page.kind) {
    case "rank":
      return (
        <Choices
          items={answer.ranked.map((o, i) => RANKED_PILL(i + 1, o))}
          other={answer.other}
        />
      );
    case "multi":
      return <Choices items={answer.selected} other={answer.other} />;
    case "twoPart":
      return (
        <div className="tw-grid tw-gap-4">
          {PART_KEYS.map((part) => (
            <div key={part} className="tw-grid tw-gap-2">
              <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">
                {Q3_PARTS[part].read}
              </p>
              <Choices
                items={answer[part].selected}
                other={answer[part].other}
                otherLabel={Q3_PARTS[part].other}
              />
            </div>
          ))}
        </div>
      );
    case "multiExclusive":
      return (
        <div className="tw-grid tw-gap-3">
          <Pills items={answer.selected} />
          {answer.plans && <Pairs pairs={[[Q4_PLANS_LABEL, answer.plans]]} />}
        </div>
      );
    case "amounts": {
      const total = answer.lines.reduce(
        (sum, l) => sum + (amountValue(l.amount) || 0),
        0,
      );
      const rows = [
        ...answer.lines.map((l) => [l.name, l.amount]),
        [AMOUNT_TOTAL, total],
      ];
      return (
        <dl className="tw-m-0 tw-grid">
          {rows.map(([name, value], i) => (
            <div
              key={name}
              className={cn(
                "tw-flex tw-justify-between tw-gap-4 tw-border-0 tw-border-solid tw-border-divider tw-py-3 tw-text-sm",
                i < rows.length - 1 ? "tw-border-b" : "tw-font-bold",
              )}
            >
              <dt>{name}</dt>
              <dd className="tw-m-0 tw-font-bold">
                $
                {i === rows.length - 1
                  ? formatMoney(value)
                  : formatAmount(value)}
              </dd>
            </div>
          ))}
        </dl>
      );
    }
    case "aboutYou": {
      const [question, ...prompts] = page.questions[0].options;
      const worked =
        answer.workedWithAdvisor === true
          ? YES
          : answer.workedWithAdvisor === false
            ? NO
            : null;
      return (
        <Pairs
          pairs={[
            [question, worked],
            ...ABOUT_YOU_FIELDS.map((f, i) => [prompts[i], answer[f]]),
          ]}
        />
      );
    }
    default:
      return <NA />;
  }
}
